// src/pages/admin/AdminChangePassword.js
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useRoleTheme } from "../../context/RoleThemeContext";
import { KeyRound } from "lucide-react";
import api from "../../api/axios";

export default function AdminChangePassword() {
  const navigate = useNavigate();
  const { adminUser, logoutAdmin, logoutUser } = useAuth();
  const { roleTheme } = useRoleTheme();

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" }); // type: "success" | "error"

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setMessage({ text: "", type: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: "", type: "" });

    if (!formData.currentPassword) {
      setMessage({ text: "Current password is required.", type: "error" });
      return;
    }
    if (!formData.newPassword || formData.newPassword.length < 6) {
      setMessage({ text: "New password must be at least 6 characters.", type: "error" });
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ text: "New password and confirm password do not match.", type: "error" });
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/admin/users/change-password", {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      if (res.data?.status === "success") {
        setMessage({ text: "Password updated successfully. Redirecting to login...", type: "success" });
        setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setTimeout(async () => {
          await logoutAdmin();
          await logoutUser();
          const baseUrl = window.location.origin;
          window.location.href = `${baseUrl}/login`;
        }, 2000);
      } else {
        setMessage({ text: res.data?.message || "Failed to change password.", type: "error" });
      }
    } catch (err) {
      setMessage({
        text: err.response?.data?.message || "Error changing password. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const primaryBtnClasses = roleTheme?.colors?.buttons?.primary || "bg-gradient-to-r from-blue-600 to-blue-700";

  return (
    <div className="min-h-screen bg-gray-100 flex p-4">
      <main className="flex-1 max-w-xl mx-auto">
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Change Admin Password</h1>
          <p className="text-gray-600 mt-1">
            Change your admin account password. For security, use a strong password.
          </p>
        </header>

        <div
          className="bg-white rounded-2xl shadow-md p-6 border border-gray-100"
          style={{
            background: roleTheme?.gradientBg?.primary || "linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)",
            borderColor: roleTheme?.colors?.sidebar?.hover || "#e5e7eb",
          }}
        >
          <div className="flex items-center gap-3 mb-6">
            <KeyRound className="w-10 h-10 text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-800">Update your password</h2>
          </div>

          {/* Message */}
          {message.text && (
            <div
              className={`mb-4 px-4 py-3 rounded-lg ${
                message.type === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
              }`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current password</label>
              <input
                type="password"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Enter current password"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoComplete="current-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
              <input
                type="password"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter new password (min 6 characters)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoComplete="new-password"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className={`${primaryBtnClasses} text-white px-6 py-2 rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50`}
              >
                {submitting ? "Updating..." : "Update password"}
              </button>
              <button
                type="button"
                onClick={() => navigate(adminUser?.userType === "M" ? "/master/dashboard" : "/admin/dashboard")}
                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg font-semibold hover:bg-gray-300 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
